import axios from "axios";
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from "../domain/account";
import {
  getRequiredPublicConfigValue,
  PUBLIC_CONFIG_KEYS,
} from "../config/publicConfig";

const api = axios.create();

const REFRESH_ENDPOINT = "/api/auth/token/refresh/";

let initializePromise;
let refreshPromise;

const normalizeBaseUrl = (baseUrl) => baseUrl.replace(/\/$/, "");

const shouldRefreshAuth = (error) => {
  const status = error?.response?.status;
  const errorData = error?.response?.data;

  return (
    status === 401 ||
    (status === 403 &&
      (errorData?.code === "token_not_valid" ||
        errorData?.detail === "Given token not valid for any token type" ||
        Array.isArray(errorData?.messages)))
  );
};

const refreshAccessToken = async () => {
  if (!refreshPromise) {
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      throw new Error("Missing refresh token");
    }

    refreshPromise = axios
      .post(`${api.defaults.baseURL}${REFRESH_ENDPOINT}`, {
        refresh: refreshToken,
      })
      .then(({ data }) => {
        setTokens(data.access, data.refresh);
        return data.access;
      })
      .finally(() => {
        refreshPromise = undefined;
      });
  }

  return refreshPromise;
};

export const initializeApi = async () => {
  if (api.defaults.baseURL) {
    return;
  }

  if (!initializePromise) {
    initializePromise = getRequiredPublicConfigValue(PUBLIC_CONFIG_KEYS.apiUrl)
      .then((baseUrl) => {
        api.defaults.baseURL = normalizeBaseUrl(baseUrl);
      })
      .catch((error) => {
        console.error("Failed to initialize API configuration:", error);
        throw error;
      })
      .finally(() => {
        initializePromise = undefined;
      });
  }

  await initializePromise;
};

api.interceptors.request.use(
  async (config) => {
    await initializeApi();

    if (!config.baseURL) {
      config.baseURL = api.defaults.baseURL;
    }

    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (originalRequest?.url?.includes(REFRESH_ENDPOINT)) {
      clearTokens();
      return Promise.reject(error);
    }

    if (
      shouldRefreshAuth(error) &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const accessToken = await refreshAccessToken();

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        clearTokens();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
